import {
  Button,
  ButtonSize,
  ButtonType,
  ColorPicker,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@chillibean/component-library';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import { handleFieldError } from '../../../../../common/errors';
import { PRESET_COLORS } from '../../utils';

export function DefinitionModal(props) {
  const {
    definition = undefined,
    requestErrors = undefined,
    requestSuccessful,
    visible,
    onClose,
    onSubmit,
    loading = false,
  } = props;

  // HOOKS
  // React
  const modalRef = useRef();
  const [formSubmitted, setFormSubmitted] = useState(false);
  // Form
  const { getValues, register, reset, setValue, trigger, watch } = useForm();

  const defaultValues = {
    name: definition?.name || '',
    color: definition?.color || PRESET_COLORS[0],
  };

  const initializeForm = () => {
    register('name', {
      defaultValue: defaultValues.name,
      value: defaultValues.name,
    });
    register('color', {
      defaultValue: defaultValues.color,
      value: defaultValues.color,
    });
  };

  const handleClose = () => {
    reset(defaultValues);
    onClose();
    setFormSubmitted(false);
  };

  const handleUpdateValue = (key, value) => {
    setValue(key, value, { shouldDirty: true });
    setFormSubmitted(false);
  };

  const handleSubmit = async () => {
    const valid = await trigger();
    if (valid) {
      onSubmit(getValues());
    }
    setFormSubmitted(true);
  };

  const getFieldError = (value) => {
    if (!formSubmitted) return null;

    return handleFieldError(requestErrors, value);
  };

  // EFFECTS
  useEffect(() => {
    reset(defaultValues);
    initializeForm();
  }, [definition]);

  useEffect(() => {
    if (requestSuccessful && formSubmitted) {
      handleClose();
    }
  }, [requestSuccessful]);

  return (
    <Modal ref={modalRef} visible={visible} onClose={handleClose}>
      <ModalHeader onClose={handleClose} title={definition ? 'Update Chapter Definition' : 'Add Chapter Definition'} />
      <BodyContainer>
        <StyledModalBody>
          <Input
            error={getFieldError('name')}
            id="name"
            label="Name"
            name="Name"
            onChange={(event) => handleUpdateValue('name', event.target.value)}
            required
            value={watch('name')}
          />
          <ColorPicker
            defaultColor={defaultValues.color}
            onSave={(color) => handleUpdateValue('color', color)}
            portals={{ overlay: modalRef.current }}
            presetColors={PRESET_COLORS}
          />
        </StyledModalBody>
      </BodyContainer>
      <ModalFooter>
        <Button onClick={handleClose} size={ButtonSize.LARGE} text="Cancel" type={ButtonType.TERTIARY} />
        <Button
          onClick={handleSubmit}
          size={ButtonSize.LARGE}
          text={definition ? 'Update' : 'Save'}
          loading={loading}
        />
      </ModalFooter>
    </Modal>
  );
}

const BodyContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 42px 24px;
`;
const StyledModalBody = styled(ModalBody)`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
  max-width: 450px;
  overflow: visible;
`;

DefinitionModal.propTypes = {
  definition: PropTypes.shape({
    name: PropTypes.string,
    color: PropTypes.string,
  }),
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  requestErrors: PropTypes.shape({
    data: PropTypes.shape({
      errors: PropTypes.shape({}),
    }),
  }),
  requestSuccessful: PropTypes.bool.isRequired,
  visible: PropTypes.bool.isRequired,
};

export default DefinitionModal;
